import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {saveAs as importedSaveAs} from 'file-saver';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {IncidentResponse} from '../../_models/incidentResponse';
import {IncidentResponseService} from '../../_services/incidentResponse.service';
import {
  AlertService,
  AuthenticationService,
  FileBlobService, UserService,
} from '../../_services/index';
import {LoginHelper} from '../../helpers/index';
import {User} from "../../_models";
import { Constants } from '../../helpers/constants';

@Component({
    standalone: false,
  selector: 'editincidentresponse-component',
  templateUrl: 'editIncidentResponse.component.html',
  styleUrls: ['./editIncidentResponse.component.css'],
})
export class EditIncidentResponseComponent implements OnInit {
  incidentResponse: IncidentResponse;
  incidentResponseId: number;
  form: FormGroup;
  visibility: any = {};
  users: User[] = [];
  currentUserId = 0;
  constants = Constants;

  attachmentCols: any[] = [
    {'field': 'date', 'header': 'Date', width: '8em'},
    {'field': 'name', 'header': 'Name'},
  ];

  types = [
    'Erroneous release of information by fax/email',
    'Theft of CUI/ePHI/Sensitive Data',
    'Theft of device containing CUI/ePHI/Sensitive Data',
    'CUI/ePHI/Senstive Data sent without encryption',
    'Unauthorized person had access to CUI/ePHI/Sensitive Data',
    'Employee(s) not following company procedures for CUI/ePHI/Sensitive Data',
    'Known business associated breach',
    'Loss of device containing CUI/ePHI/Senstive Data',
    'Other',
  ];

  constructor(
    public authenticationService: AuthenticationService,
    public incidentResponseService: IncidentResponseService,
    public fileBlobService: FileBlobService,
    public userService: UserService,
    public alertService: AlertService,
    public formBuilder: FormBuilder,
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
  }

  ngOnInit() {
    this.incidentResponse =
      this.config.data.incidentResponse || new IncidentResponse();
    this.form = this.formBuilder.group({
      incidentResponseId: [0],
      incidentResponseNumber: ['', Validators.required],
      name: [''],
      type: [''],
      occuredDate: [null],
      reportedDate: [null],
      occuredDateValue: [null],
      reportedDateValue: [null],
      severityLevel: [''],
      reportedBy: [''],
      systemsAndServicesImpacted: [''],
      initialReporting: [''],
      initialReportingValue: [''],
      incidentSequenceOfEvents: [''],
      incidentSequenceOfEventsValue: [''],
      incidentMitigation: [''],
      incidentMitigationValue: [''],
      documentation: [''],
      documentationValue: [''],
      recommendations: [''],
      recommendationsValue: [''],
      additionalCommentsNotes: [''],
      additionalCommentsNotesValue: [''],
      attachments: [[]],
      lastUpdated: [null],
      approver1Id: [null],
      approver2Id: [null],
      approved1Date: [null],
      approved2Date: [null],
      isApproved1: [false],
      isApproved2: [false],
    });

    this.form.setValue({
      name: '',
      approver1Id: null,
      approver2Id: null,
      approved1Date: null,
      approved2Date: null,
      lastUpdated: null,
      incidentResponseNumber: '',
      type: '',
      occuredDate: null,
      reportedDate: null,
      occuredDateValue: null,
      reportedDateValue: null,
      severityLevel: '',
      reportedBy: '',
      systemsAndServicesImpacted: '',
      initialReporting: '',
      initialReportingValue: '',
      incidentSequenceOfEvents: '',
      incidentSequenceOfEventsValue: '',
      incidentMitigation: '',
      incidentMitigationValue: '',
      documentation: '',
      documentationValue: '',
      recommendations: '',
      recommendationsValue: '',
      additionalCommentsNotes: '',
      additionalCommentsNotesValue: '',
      isApproved1: false,
      isApproved2: false,
      ...this.incidentResponse
    });

    this.currentUserId = JSON.parse(sessionStorage.getItem('currentUser')).userId;

    this.userService.getAll().subscribe(u => this.users = [{userId: null, fullName: 'None'}, ...u]);

    const occuredDateValue = (this.incidentResponse as any)
      ?.occuredDateValue as string;
    const reportedDateValue = (this.incidentResponse as any)
      ?.reportedDateValue as string;

    if (occuredDateValue) {
      this.form.patchValue({occuredDate: occuredDateValue});
    }
    if (reportedDateValue) {
      this.form.patchValue({reportedDate: reportedDateValue});
    }
  }

  onSave(saveAsNew = false) {
    if (!this.form.valid) {
      Object.keys(this.form.controls).forEach((field) => {
        const control = this.form.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }

    this.alertService.clear();

    const data = {...this.form.value};
    if (saveAsNew) {
      data.incidentResponseId = 0;
    }

    this.incidentResponseService.saveIncidentResponse(data).subscribe(
      (data) => {
        this.onClose();
      },
      (error) => {
        const message = 'An error occurred while updating item.';
        this.alertService.error(message);
      }
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }

  addToken(html) {
    return html
      ? html.replace(
        /api\/attachments\/file\?/gi,
        'api/attachments/file?token=' +
        LoginHelper.getFileToken() +
        '&'
      )
      : html;
  }

  generateReport(id) {
    this.incidentResponseService.generateReport(id).subscribe(
      (blob) => {
        importedSaveAs(new Blob([blob]), 'IncidentResponse.docx');
      },
      (error) => {
        const errorMessage = 'Error during generation';
        this.alertService.error(errorMessage);
      }
    );
  }

  public DownloadAttachment(fileName: string, isPath: boolean = false) {
    this.fileBlobService.downloadFile(fileName, 'IncidentResponse').subscribe(
      (blob) => {
        importedSaveAs(new Blob([blob]), fileName);
      },
      (error) => {
        const errorMessage =
          error.status == 401
            ? "The logged in user does not have access to the file's location. "
            : 'The referenced file no longer exists in the document library.';
        this.alertService.error(errorMessage);
      }
    );
  }

  deleteAssessmentAttachment(assessmentNote, item) {
    this.fileBlobService
      .deleteIncidentResponseAttachment(item.attachmentId)
      .subscribe(
        (res) => {
          this.incidentResponse.attachments.splice(
            this.incidentResponse.attachments.indexOf(item),
            1
          );
        },
        (error) => {
          const errorMessage = 'An error occurred while deleting attachment.';
          this.alertService.error(errorMessage);
        }
      );
  }

  public AddAssessmentAttachment(event) {
    let attachments = event.target.files;
    if (attachments.length > 0) {
      let file: File = attachments[0];
      this.fileBlobService
        .postIncidentResponseAttachment(
          file,
          this.incidentResponse.incidentResponseId
        )
        .subscribe(
          (res) => {
            this.form.value.attachments = this.form.value.attachments || [];
            this.form.value.attachments.push(res);
          }
        );
    }
  }
}
